import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Card, PageHeader } from "../components/account-ui.tsx";
import { OrderLines, QuoteTotals } from "../components/legacy/OrderSummary.tsx";
import { CheckIcon } from "../icons/index.tsx";
import { api, type CustomerAddress, type Quote } from "../lib/api.ts";
import { useAddresses } from "./account.address-book.tsx";

export const Route = createFileRoute("/checkout/$campaignId")({ component: Checkout });

const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
const stripePromise = publishableKey ? loadStripe(publishableKey) : null;

export type CheckoutStep = "SHIPPING" | "PAYMENT" | "CONFIRMATION";

const STEPS: { name: string; state: CheckoutStep }[] = [
  { name: "Shipping", state: "SHIPPING" },
  { name: "Payment", state: "PAYMENT" },
  { name: "Confirmation", state: "CONFIRMATION" },
];

/** Numbered rail shared by checkout and the confirmation page. */
export function CheckoutSteps({ current }: { current: CheckoutStep }) {
  const at = STEPS.findIndex((s) => s.state === current);
  return (
    <ol className="mb-8 flex items-center justify-center gap-2 sm:gap-3">
      {STEPS.map((s, index) => {
        const done = index < at;
        const active = index === at;
        return (
          <li key={s.state} className="flex items-center gap-2 sm:gap-3">
            <span
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                done || active ? "bg-primary-600 text-white" : "bg-gray-200 text-gray-500"
              }`}
            >
              {done ? <CheckIcon className="h-4 w-4" /> : index + 1}
            </span>
            <span
              className={`text-sm font-medium ${active ? "text-gray-900" : "text-gray-500"} hidden sm:inline`}
            >
              {s.name}
            </span>
            {index < STEPS.length - 1 && <span className="h-px w-6 bg-gray-300 sm:w-10" />}
          </li>
        );
      })}
    </ol>
  );
}

interface CheckoutResponse {
  orderId: string;
  code: string;
  quote: Quote;
  /** Null when the server runs with PAYMENTS_MOCK: no card, pay with one click. */
  clientSecret: string | null;
}

/**
 * The Vendure checkout: step rail, billing details on the left, order
 * summary on the right, then the Stripe payment element. The order and
 * PaymentIntent are created once on mount by /api/orders/checkout.
 */
function Checkout() {
  const { campaignId } = Route.useParams();
  const [step, setStep] = useState<CheckoutStep>("SHIPPING");
  const [session, setSession] = useState<CheckoutResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: () => api.post<CheckoutResponse>("/orders/checkout", { campaignId }),
    onSuccess: setSession,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Could not start checkout"),
  });

  // Creating the order also creates the PaymentIntent, so it runs once per
  // campaign. The ref stops StrictMode's double mount from racing two orders.
  const started = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once per campaignId
  useEffect(() => {
    if (started.current === campaignId) return;
    started.current = campaignId;
    start.mutate();
  }, [campaignId]);

  const { data: order } = useQuery({
    queryKey: ["order", session?.code],
    enabled: Boolean(session),
    queryFn: () =>
      api.get<{
        items: {
          kind: string;
          description: string;
          quantity: number;
          unit_cents: number;
          total_cents: number;
        }[];
      }>(`/orders/${session!.code}`),
  });

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <PageHeader
          title="Checkout"
          subtitle={
            session ? (
              <>
                Order <span className="font-semibold text-gray-700">{session.code}</span>
              </>
            ) : (
              "Preparing your order"
            )
          }
        />

        {error ? (
          <Card>
            <p className="text-sm font-medium text-amber-800">{error}</p>
          </Card>
        ) : !session ? (
          <p className="text-sm text-gray-500">Preparing checkout…</p>
        ) : (
          <>
            <CheckoutSteps current={step} />
            <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
              <div>
                {step === "SHIPPING" ? (
                  <Shipping onForward={() => setStep("PAYMENT")} />
                ) : session.clientSecret === null ? (
                  <MockPayment code={session.code} onBack={() => setStep("SHIPPING")} />
                ) : stripePromise ? (
                  <Elements stripe={stripePromise} options={{ clientSecret: session.clientSecret }}>
                    <PaymentForm code={session.code} />
                  </Elements>
                ) : (
                  <Card title="Payment">
                    <p className="text-sm text-amber-800">
                      Stripe is not configured. Set <code>VITE_STRIPE_PUBLISHABLE_KEY</code> to take
                      payments.
                    </p>
                    <p className="mt-2 text-sm text-gray-500">
                      Order {session.code} has been created and is awaiting payment.
                    </p>
                  </Card>
                )}
              </div>
              <Card
                title="Order summary"
                className="lg:sticky lg:top-[calc(var(--header-h)+0.75rem)]"
              >
                {order && <OrderLines items={order.items} campaignId={campaignId} />}
                <QuoteTotals quote={session.quote} />
                <p className="text-xs text-gray-500">Payments processed by Stripe.</p>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Shipping({ onForward }: { onForward: () => void }) {
  const { data } = useAddresses();
  const [selected, setSelected] = useState<string>("");
  const addresses = data?.addresses ?? [];
  useEffect(() => {
    if (!selected && addresses[0])
      setSelected(addresses.find((a) => a.default_billing)?.id ?? addresses[0].id);
  }, [addresses, selected]);

  return (
    <Card title="Billing details">
      <p className="text-sm text-gray-500">
        These details are for your invoice. Your mail is delivered to the addresses in the campaign.
      </p>
      {addresses.length > 0 ? (
        <div className="mt-5 grid gap-3">
          {addresses.map((a) => (
            <AddressOption
              key={a.id}
              address={a}
              selected={selected === a.id}
              onSelect={() => setSelected(a.id)}
            />
          ))}
        </div>
      ) : (
        <p className="mt-5 text-sm text-gray-500">
          No organisation locations.{" "}
          <Link
            to="/account/address-book/$id"
            params={{ id: "add" }}
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            Add one
          </Link>{" "}
          so the invoice has somewhere to go, or continue without.
        </p>
      )}
      <button
        type="button"
        className="mt-6 flex w-full items-center justify-center rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-700"
        onClick={onForward}
      >
        Continue to Payment
      </button>
    </Card>
  );
}

function AddressOption({
  address,
  selected,
  onSelect,
}: {
  address: CustomerAddress;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      className={`flex cursor-pointer gap-3 rounded-xl border p-4 transition-colors ${
        selected ? "border-primary-600 bg-primary-50" : "border-gray-200 hover:border-gray-300"
      }`}
    >
      <input
        type="radio"
        name="billing"
        checked={selected}
        onChange={onSelect}
        className="mt-1 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      <span className="min-w-0 text-sm">
        <span className="block font-semibold text-gray-900">
          {address.full_name || "Address"}
          {address.company && (
            <span className="font-normal text-gray-500"> · {address.company}</span>
          )}
        </span>
        <span className="mt-0.5 block text-gray-500">
          {address.street_line1}, {address.city}
          {address.province ? ` ${address.province}` : ""} {address.postal_code}
        </span>
        {(address.default_billing === 1 || address.default_shipping === 1) && (
          <span className="mt-2 flex gap-1.5">
            {address.default_billing === 1 && <Tag>Default billing</Tag>}
            {address.default_shipping === 1 && <Tag>Default location</Tag>}
          </span>
        )}
      </span>
    </label>
  );
}

function Tag({ children }: { children: string }) {
  return (
    <span className="inline-flex rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary-700">
      {children}
    </span>
  );
}

/** Dev checkout: marks the order paid on the server exactly as the Stripe webhook would. */
function MockPayment({ code, onBack }: { code: string; onBack: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pay = useMutation({
    mutationFn: () => api.post(`/orders/${code}/mock-pay`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["order", code] });
      navigate({ to: "/checkout/confirmation/$code", params: { code } });
    },
  });
  return (
    <Card title="Payment">
      <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <span className="font-semibold">Test mode:</span> no card is charged. Payments are mocked
        because <code>PAYMENTS_MOCK</code> is on and no Stripe key is set.
      </p>
      {pay.error && <p className="mt-4 text-sm font-medium text-amber-800">{pay.error.message}</p>}
      <button
        type="button"
        className="mt-6 flex w-full items-center justify-center rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
        disabled={pay.isPending}
        onClick={() => pay.mutate()}
      >
        {pay.isPending ? "Processing…" : "Pay Now (Test Mode)"}
      </button>
      <button
        type="button"
        className="mt-2 flex w-full items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50"
        onClick={onBack}
      >
        Back to Billing Details
      </button>
    </Card>
  );
}

function PaymentForm({ code }: { code: string }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  return (
    <Card title="Payment">
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!stripe || !elements) return;
          setBusy(true);
          setMessage(null);
          const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
              return_url: `${window.location.origin}/checkout/confirmation/${code}`,
            },
            // Stay on the page when no redirect is required, so we control the
            // success path. The webhook is what actually marks the order paid.
            redirect: "if_required",
          });
          setBusy(false);
          if (error) {
            setMessage(error.message ?? "Payment failed");
            return;
          }
          navigate({ to: "/checkout/confirmation/$code", params: { code } });
        }}
      >
        <PaymentElement />
        {message && <p className="mt-4 text-sm font-medium text-amber-800">{message}</p>}
        <button
          type="submit"
          className="mt-6 flex w-full items-center justify-center rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
          disabled={!stripe || busy}
        >
          {busy ? "Processing…" : "Pay Now"}
        </button>
        <p className="mt-2 text-xs text-gray-500">
          Order {code} is confirmed once Stripe notifies us, usually within seconds.
        </p>
      </form>
    </Card>
  );
}
